import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiLogOut,
  FiMusic,
  FiEdit,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCalendar,
  FiRefreshCw
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import SearchBar from "./SearchBar";
import SearchBar2 from "./SearchBar2";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [songCount, setSongCount] = useState(0);
  const [songs, setSongs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [songsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [lastSync, setLastSync] = useState(null);

  // Primary color for the theme
  const primaryColor = "#3b82f6"; // A professional blue color

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin");
    if (!isAdmin) {
      navigate("/admin");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchSongCount = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongsCount`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setSongCount(data.count);
      } catch (error) {
        console.error("Error fetching song count:", error);
      }
    };

    const fetchLastSync = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sync/last-sync`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        // Check if data exists and has the expected structure
        if (data && (data.data || data)) {
          // Handle both possible response structures
          const syncData = data.data || data;
          
          // If syncData is an array, take the most recent entry
          if (Array.isArray(syncData) && syncData.length > 0) {
            // Sort by timestamp in descending order (newest first)
            const sortedSyncs = [...syncData].sort((a, b) => 
              new Date(b.timestamp) - new Date(a.timestamp)
            );
            setLastSync(sortedSyncs[0]);
          } else {
            // If it's a single object, use it directly
            setLastSync(syncData);
          }
        }
      } catch (error) {
        console.error("Error fetching last sync info:", error);
      }
    };

    fetchSongCount();
    fetchLastSync();
  }, []);

  useEffect(() => {
    if (activeTab === "songs") {
      fetchSongs(currentPage, songsPerPage);
    }
  }, [activeTab, currentPage, songsPerPage]);

  const fetchSongs = async (page, limit) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongs?page=${page}&limit=${limit}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSongs(data.songs);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error("Error fetching songs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchSongs(newPage, songsPerPage);
  };

  const handleLogout = () => {
    localStorage.removeItem("isAdmin");
    navigate("/admin");
  };

  const handleEditSong = (song) => {
    navigate(`/edit-song/${song.songID}`);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t("admin.never");
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Calculate next scheduled sync time (Sunday at 2:00 AM)
  const getNextScheduledSync = () => {
    const now = new Date();
    const nextSync = new Date(now);
    nextSync.setDate(now.getDate() + (7 - now.getDay()) % 7); // Next Sunday
    nextSync.setHours(2, 0, 0, 0); // 2:00 AM
    
    // If today is Sunday and it's before 2 AM, set to today at 2 AM
    if (now.getDay() === 0 && now.getHours() < 2) {
      nextSync.setDate(now.getDate());
    }
    
    // If we've already passed Sunday 2 AM, move to next Sunday
    if (nextSync <= now) {
      nextSync.setDate(nextSync.getDate() + 7);
    }
    
    return formatDate(nextSync);
  };

  const stats = [
    {
      icon: <FiMusic />,
      title: t("admin.totalSongs"),
      value: songCount.toString(),
    },
    {
      icon: <FiClock />,
      title: t("admin.lastSync"),
      value: lastSync ? formatDate(lastSync.timestamp) : t("admin.never"),
    },
    {
      icon: <FiCalendar />,
      title: t("admin.nextScheduledSync"),
      value: getNextScheduledSync(),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-black">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">
            {t("admin.dashboard")}
          </h1>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors shadow-sm"
          >
            <FiLogOut />
            <span className={`${i18n.language === "ar" ? "mr-2" : "ml-2"}`}>
              {t("admin.logout")}
            </span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 transform hover:scale-105 transition-transform duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="text-4xl text-gray-800">{stat.icon}</div>
                <span className={`text-sm`}>{stat.change}</span>
              </div>
              <h3 className="text-gray-600 mt-4">{stat.title}</h3>
              <p className="text-2xl font-bold mt-2 text-black">{stat.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <div className="flex space-x-4 mb-6">
            {["overview", "songs", "settings"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab
                    ? "bg-gray-800 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {t(`admin.${tab}`)}
              </button>
            ))}
          </div>

          <div className="p-4">
            {activeTab === "overview" && (
              <div className="text-gray-800">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">
                  {t("admin.welcome")}
                </h3>
                <p className="mb-6">{t("admin.manage")}</p>

                {lastSync && (
                  <div className="mb-6 bg-white border border-gray-200 rounded-xl shadow-sm p-6">
                    <h4 className="text-lg font-semibold mb-4 flex items-center">
                      <FiRefreshCw className="mr-2" /> {t("admin.syncStatus")}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">{t("admin.lastSyncTime")}:</p>
                        <p className="font-medium">{formatDate(lastSync.timestamp)}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">{t("admin.syncType")}:</p>
                        <p className="font-medium capitalize">{lastSync.syncType}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">{t("admin.songsAdded")}:</p>
                        <p className="font-medium">{lastSync.songsAdded}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">{t("admin.syncStatus")}:</p>
                        <p className={`font-medium ${lastSync.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                          {lastSync.status === 'success' ? t("admin.success") : t("admin.failed")}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg col-span-2">
                        <p className="text-sm text-gray-600">{t("admin.nextScheduledSync")}:</p>
                        <p className="font-medium">{getNextScheduledSync()}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <button
                    onClick={async () => {
                      try {
                        setIsLoading(true);
                        setProgress("");

                        const eventSource = new EventSource(
                          `${process.env.REACT_APP_API_BASE_URL}/api/sync/sync`
                        );

                        eventSource.onmessage = (event) => {
                          const data = JSON.parse(event.data);
                          if (data.message) {
                            setProgress((prev) => prev + "\n" + data.message);
                          }
                          if (data.error) {
                            setProgress((prev) => prev + "\n❌ " + data.error);
                          }
                          
                          // If sync completed, refresh the last sync info
                          if (data.message && data.message.includes("completed successfully")) {
                            fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sync/last-sync`)
                              .then(response => response.json())
                              .then(data => {
                                // Check if data exists and has the expected structure
                                if (data && (data.data || data)) {
                                  // Handle both possible response structures
                                  const syncData = data.data || data;
                                  
                                  // If syncData is an array, take the most recent entry
                                  if (Array.isArray(syncData) && syncData.length > 0) {
                                    // Sort by timestamp in descending order (newest first)
                                    const sortedSyncs = [...syncData].sort((a, b) => 
                                      new Date(b.timestamp) - new Date(a.timestamp)
                                    );
                                    setLastSync(sortedSyncs[0]);
                                  } else {
                                    // If it's a single object, use it directly
                                    setLastSync(syncData);
                                  }
                                }
                              })
                              .catch(error => console.error("Error fetching updated sync info:", error));
                          }
                        };

                        eventSource.onerror = () => {
                          eventSource.close();
                          setIsLoading(false);
                        };
                      } catch (error) {
                        console.error("Sync error:", error);
                        setProgress(
                          (prev) => prev + "\n❌ Error: " + error.message
                        );
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                    className={`px-6 py-3 rounded-lg font-medium text-white transition-colors ${
                      isLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gray-800 hover:bg-gray-700 shadow-sm"
                    }`}
                  >
                    {isLoading ? t("admin.syncing") : t("admin.sync")}
                  </button>

                  {progress && (
                    <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                        {progress}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "songs" && (
              <div className="text-gray-800">
                <SearchBar2 navigationType="edit" />

                <h3 className="text-xl font-semibold mb-4 text-gray-800">
                  {t("admin.manageSongs")}
                </h3>

                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
                  </div>
                ) : songs.length > 0 ? (
                  <div className="space-y-4">
                    {songs.map((song) => (
                      <div
                        key={song.songID}
                        className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-800">
                              {song.title}
                            </h4>
                            <p className="text-sm text-gray-500 mt-1">
                              ID: {song.songID}
                            </p>
                          </div>
                          <button
                            onClick={() => handleEditSong(song)}
                            className="px-4 py-2 bg-gray-800 text-white rounded-lg flex items-center hover:bg-gray-700 transition-colors shadow-sm"
                          >
                            <FiEdit className="mr-2" />
                            {t("admin.edit")}
                          </button>
                        </div>
                        {song.chorus && song.chorus.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-sm text-gray-600 line-clamp-1">
                              <span className="font-medium text-gray-700">
                                {t("admin.chorus")}:
                              </span>{" "}
                              {song.chorus[0]}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="flex justify-between items-center mt-6">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-full ${
                          currentPage === 1
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-gray-800 hover:bg-gray-100"
                        }`}
                      >
                        <FiChevronLeft size={24} />
                      </button>
                      <span className="text-gray-600">
                        {t("admin.page")} {currentPage} {t("admin.of")}{" "}
                        {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-full ${
                          currentPage === totalPages
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-gray-800 hover:bg-gray-100"
                        }`}
                      >
                        <FiChevronRight size={24} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {t("admin.noSongsFound")}
                  </div>
                )}
              </div>
            )}

            {activeTab === "settings" && (
              <div className="text-gray-800">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">
                  {t("admin.settings")}
                </h3>
                <p>{t("admin.settingsDescription")}</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;